import { AdvancedSettings, Location } from "../types.ts";
import { cache } from "../utils/cache.ts";
import { fetchCitySuggestions, geocode } from "../utils/geocoding.ts";
import { popularCities, showError, showSuccess } from "../utils/helpers.ts";
import { langNames, t } from "../i18n.ts";
import { MapController } from "../map/MapController.ts";
import { PosterRenderer } from "../poster/PosterRenderer.ts";
import { ThemeManager } from "../theme/ThemeManager.ts";
import { GalleryManager } from "../gallery/GalleryManager.ts";
import { PodShopController } from "./PodShopController.ts";

export class App {
  public canvas: HTMLCanvasElement;
  public ctx: CanvasRenderingContext2D;

  public mapController: MapController;
  public posterRenderer: PosterRenderer;
  public themeManager: ThemeManager;
  public galleryManager: GalleryManager;
  public podShop: PodShopController;
  public cache = cache;

  public currentPosterId: string | null = null;
  public autocompleteTimeout: any = null;
  public selectedSuggestionIndex = -1;
  public printFormat = "vertical";
  public isGenerating = false;
  public currentEventSource: any = null;
  public currentLang = localStorage.getItem("lang") || "en";

  public advancedSettings: AdvancedSettings = {
    customCity: "",
    customCountry: "",
    customLat: "",
    customLon: "",
    fontFamily: "Geometric Humanist",
    showBuildings: true,
    showRailways: true,
  };

  constructor() {
    const canvas = document.getElementById("poster-canvas") as HTMLCanvasElement;
    if (!canvas) throw new Error("Canvas element not found");
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get 2D context");
    this.ctx = ctx;

    this.mapController = new MapController(this);
    this.posterRenderer = new PosterRenderer(this);
    this.themeManager = new ThemeManager(this);
    this.galleryManager = new GalleryManager(this);
    this.podShop = new PodShopController((printScale: number) =>
      this.exportDesignAsset(printScale)
    );

    this.init();
  }

  async init() {
    await this.themeManager.loadThemes();
    this.loadStateFromURL();
    this.setupEventListeners();
    this.setupTabSwitching();
    this.setupLanguageSelector();
    this.loadCachedMapsUI();
    this.applyLanguage(this.currentLang);
    this.renderPopularCities();
    this.setupCityAutocomplete();
    this.setupPrintOptions();
    this.setupPreviewTools();
    this.setupAdvancedSettings();
    this.setupLegalModals();
    this.mapController.setupMapTab();
    await this.galleryManager.loadGallery();

    if (this.hasURLState()) {
      setTimeout(() => this.generatePoster(), 500);
    }

    // Propagate shop availability to POD controller (shows "Coming Soon" when false)
    try {
      const statusResp = await fetch("/api/pod/status");
      if (statusResp.ok) {
        const status = await statusResp.json() as {
          allowOrderPhysical: boolean;
          stripeReady?: boolean;
          stripeMode?: "test" | "live" | "none";
          webhookReady?: boolean;
        };
        this.podShop.setShopStatus(status);
      }
    } catch (_) { /* non-fatal */ }

    // Handle Stripe redirect-back
    const urlParams = new URLSearchParams(globalThis.location.search);
    const podSuccess = urlParams.get("pod_success");
    const podCancelled = urlParams.get("pod_cancelled");

    if (podSuccess) {
      globalThis.history.replaceState({}, "", globalThis.location.pathname);
      await this.podShop.completePayment(podSuccess);
    } else if (podCancelled) {
      globalThis.history.replaceState({}, "", globalThis.location.pathname);
      showError(t("pod.paymentCancelled", this.currentLang));
    }
  }

  hasURLState() {
    const params = new URLSearchParams(globalThis.location.search);
    return params.has("city") || params.has("lat");
  }

  loadStateFromURL() {
    const params = new URLSearchParams(globalThis.location.search);

    const city = params.get("city");
    const country = params.get("country");
    if (city) {
      const cityInput = document.getElementById("city") as HTMLInputElement;
      if (cityInput) cityInput.value = decodeURIComponent(city);
    }
    if (country) {
      const countryInput = document.getElementById("country") as HTMLInputElement;
      if (countryInput) countryInput.value = decodeURIComponent(country);
    }

    const lat = params.get("lat");
    const lon = params.get("lon");
    if (lat && lon) {
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lon);
      if (!isNaN(latitude) && !isNaN(longitude)) {
        this.mapController.location.lat = latitude;
        this.mapController.location.lon = longitude;
        this.mapController.location.isSet = true;
      }
    }

    const distance = params.get("distance");
    if (distance) {
      const dist = parseInt(distance);
      if (!isNaN(dist) && dist >= 1 && dist <= 15) {
        const distanceSlider = document.getElementById("distance") as HTMLInputElement;
        const distanceValue = document.getElementById("distance-value");
        if (distanceSlider) distanceSlider.value = dist.toString();
        if (distanceValue) distanceValue.textContent = `${dist} km`;
        this.mapController.location.radius = dist;
      }
    }

    const theme = params.get("theme");
    if (theme) {
      setTimeout(() => {
        const themeSelect = document.getElementById("theme") as HTMLSelectElement;
        if (themeSelect) {
          const decodedTheme = decodeURIComponent(theme);
          const options = Array.from(themeSelect.options);
          const matchingOption = options.find((opt) =>
            opt.value.toLowerCase() === decodedTheme.toLowerCase()
          );
          if (matchingOption) {
            themeSelect.value = matchingOption.value;
            this.themeManager.currentTheme = this.themeManager.themes.find((t) =>
              t.name === matchingOption.value
            ) || null;
          }
        }
      }, 100);
    }

    const format = params.get("format");
    if (format && (format === "vertical" || format === "square")) {
      this.printFormat = format;
      const previewContainer = document.getElementById("preview-container");
      if (previewContainer) {
        if (format === "square") {
          previewContainer.classList.remove("aspect-[3/4]");
          previewContainer.classList.add("aspect-square");
        } else {
          previewContainer.classList.remove("aspect-square");
          previewContainer.classList.add("aspect-[3/4]");
        }
      }
      setTimeout(() => this.setPrintFormat(format), 100);
    }

    const customCity = params.get("customCity");
    const customCountry = params.get("customCountry");
    const customLat = params.get("customLat");
    const customLon = params.get("customLon");
    const fontFamily = params.get("font");

    if (customCity) {
      this.advancedSettings.customCity = decodeURIComponent(customCity);
      const input = document.getElementById("custom-city") as HTMLInputElement;
      if (input) input.value = this.advancedSettings.customCity;
    }
    if (customCountry) {
      this.advancedSettings.customCountry = decodeURIComponent(customCountry);
      const input = document.getElementById("custom-country") as HTMLInputElement;
      if (input) input.value = this.advancedSettings.customCountry;
    }
    if (customLat) {
      this.advancedSettings.customLat = decodeURIComponent(customLat);
      const input = document.getElementById("custom-lat") as HTMLInputElement;
      if (input) input.value = this.advancedSettings.customLat;
    }
    if (customLon) {
      this.advancedSettings.customLon = decodeURIComponent(customLon);
      const input = document.getElementById("custom-lon") as HTMLInputElement;
      if (input) input.value = this.advancedSettings.customLon;
    }
    if (fontFamily) {
      const decodedFont = decodeURIComponent(fontFamily);
      this.advancedSettings.fontFamily = decodedFont;
      const select = document.getElementById("font-family") as HTMLSelectElement;
      if (select) select.value = decodedFont;
    }

    const showBuildings = params.get("buildings");
    if (showBuildings !== null) {
      this.advancedSettings.showBuildings = showBuildings === "true";
      const checkbox = document.getElementById("show-buildings") as HTMLInputElement;
      if (checkbox) checkbox.checked = this.advancedSettings.showBuildings;
    }

    const showRailways = params.get("railways");
    if (showRailways !== null) {
      this.advancedSettings.showRailways = showRailways === "true";
      const checkbox = document.getElementById("show-railways") as HTMLInputElement;
      if (checkbox) checkbox.checked = this.advancedSettings.showRailways;
    }

    const lang = params.get("lang");
    if (lang) {
      this.currentLang = lang;
      localStorage.setItem("lang", lang);
    }

    const tab = params.get("tab");
    if (tab) {
      const tabId = `${tab}-tab`;
      const tabElement = document.getElementById(tabId);
      if (tabElement) {
        setTimeout(() => {
          document.querySelectorAll(".tab-content").forEach((t) => t.classList.remove("active"));
          tabElement.classList.add("active");
          document.querySelectorAll(".tab-link").forEach((link) => {
            link.classList.remove(
              "bg-white",
              "dark:bg-slate-700",
              "shadow-sm",
              "text-primary",
              "font-semibold",
            );
            link.classList.add("text-slate-600", "dark:text-slate-400", "hover:text-primary");
          });
          const activeLink = document.querySelector(`[data-tab="${tab}"]`);
          if (activeLink) {
            activeLink.classList.add(
              "bg-white",
              "dark:bg-slate-700",
              "shadow-sm",
              "text-primary",
              "font-semibold",
            );
            activeLink.classList.remove(
              "text-slate-600",
              "dark:text-slate-400",
              "hover:text-primary",
            );
          }
        }, 100);
      }
    }
  }

  async loadCachedMapsUI() {
    try {
      const response = await fetch("/api/cached-maps");
      if (!response.ok) return;
      const data = await response.json();

      const select = document.getElementById("cached-maps-select") as HTMLSelectElement;
      const container = document.getElementById("cached-maps-container");

      if (!select || !container || !data.cachedMaps || data.cachedMaps.length === 0) return;

      container.style.display = "flex";

      data.cachedMaps.forEach((map: any) => {
        const option = document.createElement("option");
        option.value = map.filename;
        option.textContent = `${map.displayName} (${map.sizeMB} MB)`;
        // Store coordinates on the option
        if (map.displayName === "Tokyo, Japan") {
          option.dataset.lat = "35.6762";
          option.dataset.lon = "139.6503";
          option.dataset.city = "Tokyo";
          option.dataset.country = "Japan";
        } else if (map.displayName === "Barcelona, Spain") {
          option.dataset.lat = "41.3874";
          option.dataset.lon = "2.1686";
          option.dataset.city = "Barcelona";
          option.dataset.country = "Spain";
        } else if (map.displayName === "Aarhus, Denmark") {
          option.dataset.lat = "56.1496";
          option.dataset.lon = "10.2134";
          option.dataset.city = "Aarhus";
          option.dataset.country = "Denmark";
        }
        select.appendChild(option);
      });

      select.addEventListener("change", (e: any) => {
        const option = select.options[select.selectedIndex];
        if (!option.value) return;

        const cityInput = document.getElementById("city") as HTMLInputElement;
        const countryInput = document.getElementById("country") as HTMLInputElement;
        const distanceSlider = document.getElementById("distance") as HTMLInputElement;
        const distanceValue = document.getElementById("distance-value");

        if (option.dataset.city) cityInput.value = option.dataset.city;
        if (option.dataset.country) countryInput.value = option.dataset.country;

        if (option.dataset.lat && option.dataset.lon) {
          this.mapController.location.lat = parseFloat(option.dataset.lat);
          this.mapController.location.lon = parseFloat(option.dataset.lon);
          this.mapController.location.isSet = true;

          // All cached maps are 5km
          if (distanceSlider && distanceValue) {
            distanceSlider.value = "5";
            distanceValue.textContent = "5 km";
            this.mapController.location.radius = 5;
          }

          this.updateURLState();
          this.generatePoster();
        }
      });
    } catch (e) {
      console.error("Failed to load cached maps", e);
    }
  }

  updateURLState() {
    const params = new URLSearchParams();

    const cityInput = globalThis.document.getElementById("city") as HTMLInputElement;
    const countryInput = globalThis.document.getElementById("country") as HTMLInputElement;
    if (cityInput?.value) params.set("city", cityInput.value);
    if (countryInput?.value) params.set("country", countryInput.value);

    if (this.mapController.location.isSet) {
      params.set("lat", this.mapController.location.lat.toFixed(4));
      params.set("lon", this.mapController.location.lon.toFixed(4));
    }

    const distanceSlider = document.getElementById("distance") as HTMLInputElement;
    if (distanceSlider?.value) params.set("distance", distanceSlider.value);

    if (this.themeManager.currentTheme) {
      params.set("theme", this.themeManager.currentTheme.name);
    }

    if (this.printFormat !== "vertical") {
      params.set("format", this.printFormat);
    }

    const activeTab = document.querySelector(".tab-content.active");
    if (activeTab && activeTab.id) {
      const tabName = activeTab.id.replace("-tab", "");
      if (tabName !== "generate") {
        params.set("tab", tabName);
      }
    }

    if (this.advancedSettings.customCity) {
      params.set("customCity", this.advancedSettings.customCity);
    }
    if (this.advancedSettings.customCountry) {
      params.set("customCountry", this.advancedSettings.customCountry);
    }
    if (this.advancedSettings.customLat) params.set("customLat", this.advancedSettings.customLat);
    if (this.advancedSettings.customLon) params.set("customLon", this.advancedSettings.customLon);
    if (this.advancedSettings.fontFamily !== "Geometric Humanist") {
      params.set("font", this.advancedSettings.fontFamily);
    }

    if (!this.advancedSettings.showBuildings) {
      params.set("buildings", "false");
    }
    if (!this.advancedSettings.showRailways) {
      params.set("railways", "false");
    }

    if (this.currentLang !== "en") {
      params.set("lang", this.currentLang);
    }

    const newURL = params.toString()
      ? `${globalThis.location.pathname}?${params.toString()}`
      : globalThis.location.pathname;
    globalThis.history.replaceState({}, "", newURL);
  }

  setupEventListeners() {
    const form = document.getElementById("poster-form");
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.generatePoster();
      });
    }

    document.getElementById("download-png-btn")?.addEventListener(
      "click",
      () => this.posterRenderer.downloadPoster("png"),
    );
    document.getElementById("download-pdf-btn")?.addEventListener(
      "click",
      () => this.posterRenderer.downloadPoster("pdf"),
    );
    document.getElementById("download-svg-btn")?.addEventListener(
      "click",
      () => this.posterRenderer.downloadPoster("svg"),
    );
    document.getElementById("buy-physical-btn")?.addEventListener(
      "click",
      () => this.initiateBuyPhysical(),
    );

    document.getElementById("share-btn")?.addEventListener(
      "click",
      () => this.shareConfiguration(),
    );

    const themeSelect = document.getElementById("theme") as HTMLSelectElement;
    if (themeSelect) {
      themeSelect.addEventListener("change", (e: any) => {
        const themeName = e.target.value;
        this.themeManager.currentTheme = this.themeManager.themes.find((t) =>
          t.name === themeName
        ) || null;
      });
    }

    const distanceSlider = document.getElementById("distance");
    if (distanceSlider) {
      distanceSlider.addEventListener("input", (e: any) => {
        const val = document.getElementById("distance-value");
        if (val) val.textContent = `${e.target.value} km`;
      });
    }
  }

  async initiateBuyPhysical() {
    const btn = document.getElementById("buy-physical-btn") as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      const icon = btn.querySelector(".material-symbols-outlined");
      if (icon) icon.textContent = "sync";
      icon?.classList.add("animate-spin");
    }
    await this.podShop.open();
    if (btn) {
      btn.disabled = false;
      const icon = btn.querySelector(".material-symbols-outlined");
      if (icon) icon.textContent = "local_shipping";
      icon?.classList.remove("animate-spin");
    }
  }

  /**
   * Exports the current poster design as an SVG asset to the server
   * and returns the asset ID for use in POD orders.
   */
  async exportDesignAsset(printScale = 1.0): Promise<string> {
    let svgString = this.posterRenderer.generateSvgString();
    if (!svgString) throw new Error("Could not generate SVG – no map data available");

    if (printScale < 0.99) {
      svgString = this.addPrintScalePadding(svgString, printScale);
    }

    const response = await fetch("/api/pod/export-print-ready", {
      method: "POST",
      headers: { "Content-Type": "image/svg+xml" },
      body: svgString,
    });

    if (!response.ok) throw new Error("Failed to export design asset");

    const data = await response.json();
    console.log("Design asset exported:", data.assetId);
    return data.assetId;
  }

  private addPrintScalePadding(svgString: string, scale: number): string {
    const svgOpenMatch = svgString.match(/^<svg([^>]*)>/);
    if (!svgOpenMatch) return svgString;
    const attrs = svgOpenMatch[1];
    const wMatch = attrs.match(/width="([^"]+)"/);
    const hMatch = attrs.match(/height="([^"]+)"/);
    if (!wMatch || !hMatch) return svgString;
    const w = parseFloat(wMatch[1]);
    const h = parseFloat(hMatch[1]);
    if (isNaN(w) || isNaN(h)) return svgString;
    const pad = (1 - scale) / 2;
    const padX = (w * pad).toFixed(1);
    const padY = (h * pad).toFixed(1);
    const inner = svgString.slice(svgOpenMatch[0].length, -6);
    return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg"><rect width="${w}" height="${h}" fill="white"/><g transform="translate(${padX},${padY}) scale(${scale})">${inner}</g></svg>`;
  }

  setupLanguageSelector() {
    const langBtn = document.getElementById("lang-btn");
    const langDropdown = document.getElementById("lang-dropdown");
    const langOptions = document.querySelectorAll(".lang-option");

    if (!langBtn || !langDropdown) return;

    langBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      langDropdown.classList.toggle("hidden");
    });

    document.addEventListener("click", () => {
      langDropdown.classList.add("hidden");
    });

    langOptions.forEach((option) => {
      option.addEventListener("click", () => {
        const lang = (option as HTMLElement).dataset.lang;
        if (lang) {
          this.currentLang = lang;
          localStorage.setItem("lang", lang);
          this.applyLanguage(lang);
          this.updateURLState();
          langDropdown.classList.add("hidden");
        }
      });
    });
  }

  applyLanguage(lang: string) {
    const langCurrent = document.getElementById("lang-current");
    if (langCurrent) langCurrent.textContent = langNames[lang] || "English";

    document.querySelectorAll("[data-i18n]").forEach((el) => {
      const key = (el as HTMLElement).dataset.i18n;
      if (key) {
        const val = t(key, lang);
        if (val && val !== key) el.textContent = val;
      }
    });

    document.querySelectorAll("[data-i18n-placeholder]").forEach((el) => {
      const key = (el as HTMLElement).dataset.i18nPlaceholder;
      if (key) {
        const val = t(key, lang);
        if (val && val !== key) (el as HTMLInputElement).placeholder = val;
      }
    });

    // Generate button innerHTML is replaced by showLoading() and loses data-i18n — update it explicitly
    const generateBtn = document.getElementById("generate-btn");
    if (generateBtn) {
      if (this.isGenerating) {
        generateBtn.innerHTML = `<span class="material-symbols-outlined animate-spin">sync</span> ${
          t("form.stop", lang)
        }`;
      } else {
        generateBtn.innerHTML = `<span class="material-symbols-outlined">refresh</span> ${
          t("form.generate", lang)
        }`;
      }
    }

    if (this.galleryManager.galleryPosters && this.galleryManager.galleryPosters.length > 0) {
      this.galleryManager.renderGallery(this.galleryManager.getFilteredPosters());
    }
    this.podShop?.refreshLanguage();
  }

  setupPrintOptions() {
    const verticalBtn = document.getElementById("print-vertical");
    const squareBtn = document.getElementById("print-square");

    if (verticalBtn && squareBtn) {
      verticalBtn.addEventListener("click", () => this.setPrintFormat("vertical"));
      squareBtn.addEventListener("click", () => this.setPrintFormat("square"));
    }
  }

  setPrintFormat(format: string) {
    this.printFormat = format;
    const verticalBtn = document.getElementById("print-vertical");
    const squareBtn = document.getElementById("print-square");
    const previewContainer = document.getElementById("preview-container");

    if (previewContainer) {
      if (format === "square") {
        previewContainer.classList.remove("aspect-[3/4]");
        previewContainer.classList.add("aspect-square");
      } else {
        previewContainer.classList.remove("aspect-square");
        previewContainer.classList.add("aspect-[3/4]");
      }
    }

    if (verticalBtn && squareBtn) {
      if (format === "vertical") {
        verticalBtn.classList.add("border-primary", "bg-primary/5");
        verticalBtn.classList.remove("border-transparent");
        verticalBtn.querySelector("span")?.classList.add("text-primary");
        verticalBtn.querySelector("span")?.classList.remove("text-slate-500");
        verticalBtn.querySelector("div")?.classList.add("border-primary");
        verticalBtn.querySelector("div")?.classList.remove(
          "border-slate-300",
          "dark:border-slate-600",
        );

        squareBtn.classList.remove("border-primary", "bg-primary/5");
        squareBtn.classList.add("border-transparent");
        squareBtn.querySelector("span")?.classList.remove("text-primary");
        squareBtn.querySelector("span")?.classList.add("text-slate-500");
        squareBtn.querySelector("div")?.classList.remove("border-primary");
        squareBtn.querySelector("div")?.classList.add("border-slate-300", "dark:border-slate-600");
      } else {
        squareBtn.classList.add("border-primary", "bg-primary/5");
        squareBtn.classList.remove("border-transparent");
        squareBtn.querySelector("span")?.classList.add("text-primary");
        squareBtn.querySelector("span")?.classList.remove("text-slate-500");
        squareBtn.querySelector("div")?.classList.add("border-primary");
        squareBtn.querySelector("div")?.classList.remove(
          "border-slate-300",
          "dark:border-slate-600",
        );

        verticalBtn.classList.remove("border-primary", "bg-primary/5");
        verticalBtn.classList.add("border-transparent");
        verticalBtn.querySelector("span")?.classList.remove("text-primary");
        verticalBtn.querySelector("span")?.classList.add("text-slate-500");
        verticalBtn.querySelector("div")?.classList.remove("border-primary");
        verticalBtn.querySelector("div")?.classList.add(
          "border-slate-300",
          "dark:border-slate-600",
        );
      }
    }

    if (this.cache.get().data) {
      const city = (document.getElementById("city") as HTMLInputElement)?.value;
      const country = (document.getElementById("country") as HTMLInputElement)?.value;
      if (city && country) {
        this.posterRenderer.renderPoster(
          this.cache.get().data,
          city,
          country,
          {
            ...this.cache.get().location,
            radius: parseInt(
              (document.getElementById("distance") as HTMLInputElement).value || "5",
            ),
          },
        );
        this.updateURLState();
      }
    }
  }

  setupPreviewTools() {
    document.getElementById("preview-zoom")?.addEventListener("click", () => {
      const dataUrl = this.canvas.toDataURL("image/png");
      if (dataUrl && dataUrl !== "data:,") {
        const win = globalThis.open();
        if (win) {
          win.document.write(`<img src="${dataUrl}" style="max-width: 100%; height: auto;" />`);
        }
      }
    });

    document.getElementById("preview-fullscreen")?.addEventListener("click", () => {
      const container = this.canvas.closest(".aspect-\\\\[3\\\\/4\\\\]") ||
        this.canvas.closest(".aspect-square");
      if (container && container.requestFullscreen) {
        container.requestFullscreen();
      }
    });
  }

  setupTabSwitching() {
    const tabLinks = document.querySelectorAll(".tab-link");
    tabLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const tabName = (link as HTMLElement).dataset.tab;
        if (tabName) this.switchTab(tabName);
      });
    });

    document.getElementById("logo-link")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.switchTab("generate");
    });

    document.getElementById("use-my-location")?.addEventListener(
      "click",
      () => this.useCurrentLocation(),
    );

    (globalThis as any).switchTab = (tabName: string, options: any) =>
      this.switchTab(tabName, options);
    (globalThis as any).closeModal = () => this.closeModal();

    document.addEventListener("keydown", (e) => {
      const modal = document.getElementById("poster-modal");
      if (!modal || modal.classList.contains("hidden")) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.galleryManager.navigatePoster(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        this.galleryManager.navigatePoster(1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.closeModal();
      }
    });

    const modal = document.getElementById("poster-modal");
    if (modal) {
      modal.addEventListener("click", (e) => {
        if (e.target === modal) {
          this.closeModal();
        }
      });
    }
  }

  switchTab(tabName: string, options: any = {}) {
    document.querySelectorAll(".tab-link").forEach((link) => {
      if ((link as HTMLElement).dataset.tab === tabName) {
        link.classList.add(
          "bg-white",
          "dark:bg-slate-700",
          "shadow-sm",
          "text-primary",
          "font-semibold",
        );
        link.classList.remove("text-slate-600", "dark:text-slate-400", "font-medium");
      } else {
        link.classList.remove(
          "bg-white",
          "dark:bg-slate-700",
          "shadow-sm",
          "text-primary",
          "font-semibold",
        );
        link.classList.add("text-slate-600", "dark:text-slate-400", "font-medium");
      }
    });

    document.querySelectorAll(".tab-content").forEach((content) => {
      content.classList.remove("active");
    });

    const tabEl = document.getElementById(`${tabName}-tab`);
    if (tabEl) tabEl.classList.add("active");

    if (tabName === "gallery") {
      this.galleryManager.loadGallery();
    } else if (tabName === "generate" && options.theme) {
      this.selectTheme(options.theme);
    } else if (tabName === "map") {
      const distanceSlider = document.getElementById("distance") as HTMLInputElement;
      const mapRadiusSlider = document.getElementById("map-radius") as HTMLInputElement;
      const mapRadiusValue = document.getElementById("map-radius-value");
      if (distanceSlider && mapRadiusSlider) {
        const dist = parseInt(distanceSlider.value) || 5;
        this.mapController.location.radius = dist;
        mapRadiusSlider.value = dist.toString();
        if (mapRadiusValue) mapRadiusValue.textContent = `${dist} km`;
      }

      setTimeout(() => {
        this.mapController.initLeafletMap();
        if ((this.mapController as any).leafletMap) {
          (this.mapController as any).leafletMap.invalidateSize();
          if (this.mapController.location.isSet) {
            (this.mapController as any).leafletMap.setView([
              this.mapController.location.lat,
              this.mapController.location.lon,
            ], 12);
            this.mapController.updateMapMarker();
            this.mapController.updateMapCircle();
          }
        }
      }, 100);
    }

    this.updateURLState();
  }

  selectTheme(themeName: string) {
    const themeSelect = document.getElementById("theme") as HTMLSelectElement;
    if (!themeSelect) return;

    const options = Array.from(themeSelect.options);
    const matchingOption = options.find((opt) =>
      opt.value.toLowerCase() === themeName.toLowerCase()
    );

    if (matchingOption) {
      themeSelect.value = matchingOption.value;
      this.themeManager.currentTheme = this.themeManager.themes.find((t) =>
        t.name === matchingOption.value
      ) || null;
      this.cache.clear();
    }
  }

  useCurrentLocation() {
    if (!navigator.geolocation) {
      showError("Geolocation is not supported by your browser");
      return;
    }

    const geoBtn = document.getElementById("use-my-location");
    if (!geoBtn) return;
    const originalContent = geoBtn.innerHTML;
    geoBtn.innerHTML =
      '<span class="animate-spin material-symbols-outlined text-[14px]">sync</span><span>Finding...</span>';
    (geoBtn as HTMLButtonElement).disabled = true;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        this.mapController.location.lat = latitude;
        this.mapController.location.lon = longitude;
        this.mapController.location.isSet = true;

        this.mapController.updateCoordsDisplay(latitude, longitude);

        const map = (this.mapController as any).leafletMap;
        if (map) {
          map.setView([latitude, longitude], 13);
          this.mapController.updateMapMarker();
          this.mapController.updateMapCircle();
        }

        // We can do reverse geocoding via applyMapLocation or separately
        geoBtn.innerHTML = originalContent;
        (geoBtn as HTMLButtonElement).disabled = false;
      },
      (error) => {
        showError("Could not get your location: " + error.message);
        geoBtn.innerHTML = originalContent;
        (geoBtn as HTMLButtonElement).disabled = false;
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  renderPopularCities() {
    const container = document.getElementById("city-chips");
    if (!container) return;

    container.innerHTML = "";
    popularCities.forEach(({ city, country }) => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "city-chip";
      chip.textContent = city;
      chip.title = `${city}, ${country}`;
      chip.onclick = () => this.selectCity(city, country);
      container.appendChild(chip);
    });
  }

  selectCity(city: string, country: string, lat: number | null = null, lon: number | null = null) {
    (document.getElementById("city") as HTMLInputElement).value = city;
    (document.getElementById("country") as HTMLInputElement).value = country;
    this.hideSuggestions();

    this.cache.clear();

    if (lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon)) {
      this.mapController.location.lat = lat;
      this.mapController.location.lon = lon;
      this.mapController.location.isSet = true;
      this.mapController.updateCoordsDisplay(lat, lon);
    } else {
      this.mapController.location.isSet = false;
      const coordsDisplay = document.getElementById("coords-display");
      if (coordsDisplay) coordsDisplay.classList.add("hidden");
    }
  }

  setupCityAutocomplete() {
    const cityInput = document.getElementById("city") as HTMLInputElement;
    const countryInput = document.getElementById("country") as HTMLInputElement;
    if (!cityInput) return;

    const resetMapLocation = () => {
      if (this.mapController.location.isSet) {
        this.mapController.location.isSet = false;
        this.cache.clear();
        const coordsDisplay = document.getElementById("coords-display");
        if (coordsDisplay) coordsDisplay.classList.add("hidden");
      }
    };

    cityInput.addEventListener("input", (e: any) => {
      resetMapLocation();
      clearTimeout(this.autocompleteTimeout);
      const query = e.target.value.trim();

      if (query.length < 2) {
        this.hideSuggestions();
        return;
      }

      this.autocompleteTimeout = setTimeout(async () => {
        const features = await fetchCitySuggestions(query);
        this.showSuggestions(features);
      }, 300);
    });

    if (countryInput) {
      countryInput.addEventListener("input", resetMapLocation);
    }

    cityInput.addEventListener("keydown", (e) => {
      const suggestions = document.getElementById("city-suggestions");
      const items = suggestions?.querySelectorAll(".suggestion-item");
      if (!items || items.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.selectedSuggestionIndex = Math.min(this.selectedSuggestionIndex + 1, items.length - 1);
        this.updateSuggestionHighlight(items as NodeListOf<HTMLElement>);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, 0);
        this.updateSuggestionHighlight(items as NodeListOf<HTMLElement>);
      } else if (e.key === "Enter" && this.selectedSuggestionIndex >= 0) {
        e.preventDefault();
        (items[this.selectedSuggestionIndex] as HTMLElement)?.click();
      } else if (e.key === "Escape") {
        this.hideSuggestions();
      }
    });

    document.addEventListener("click", (e) => {
      if (
        !(e.target as Element).closest("#city") &&
        !(e.target as Element).closest("#city-suggestions")
      ) {
        this.hideSuggestions();
      }
    });
  }

  showSuggestions(features: any[]) {
    const container = document.getElementById("city-suggestions");
    if (!container) return;

    if (features.length === 0) {
      this.hideSuggestions();
      return;
    }

    this.selectedSuggestionIndex = -1;
    container.innerHTML = features.map((feature, index) => {
      const props = feature.properties;
      const coords = feature.geometry?.coordinates || [];
      const city = props.name || "";
      const country = props.country || "";
      const state = props.state || "";
      const subtitle = [state, country].filter(Boolean).join(", ");
      const lon = coords[0] || "";
      const lat = coords[1] || "";

      return `
        <div class="suggestion-item" data-index="${index}" data-city="${city}" data-country="${country}" data-lat="${lat}" data-lon="${lon}">
          <div class="font-medium text-slate-900 dark:text-white">${city}</div>
          <div class="text-xs text-slate-500 dark:text-slate-400">${subtitle}</div>
        </div>
      `;
    }).join("");

    container.querySelectorAll(".suggestion-item").forEach((item) => {
      (item as HTMLElement).onclick = () => {
        const ds = (item as HTMLElement).dataset;
        this.selectCity(ds.city!, ds.country!, parseFloat(ds.lat!), parseFloat(ds.lon!));
      };
    });

    container.classList.remove("hidden");
  }

  hideSuggestions() {
    const container = document.getElementById("city-suggestions");
    if (container) {
      container.classList.add("hidden");
      container.innerHTML = "";
    }
    this.selectedSuggestionIndex = -1;
  }

  updateSuggestionHighlight(items: NodeListOf<HTMLElement>) {
    items.forEach((item, index) => {
      item.classList.toggle("active", index === this.selectedSuggestionIndex);
    });
  }

  setupAdvancedSettings() {
    const customCity = document.getElementById("custom-city") as HTMLInputElement;
    const customCountry = document.getElementById("custom-country") as HTMLInputElement;
    const customLat = document.getElementById("custom-lat") as HTMLInputElement;
    const customLon = document.getElementById("custom-lon") as HTMLInputElement;
    const fontSelect = document.getElementById("font-family") as HTMLSelectElement;
    const resetBtn = document.getElementById("reset-advanced");

    if (customCity) {
      customCity.addEventListener("input", (e: any) => {
        this.advancedSettings.customCity = e.target.value;
      });
    }
    if (customCountry) {
      customCountry.addEventListener("input", (e: any) => {
        this.advancedSettings.customCountry = e.target.value;
      });
    }
    if (customLat) {
      customLat.addEventListener("input", (e: any) => {
        this.advancedSettings.customLat = e.target.value;
      });
    }
    if (customLon) {
      customLon.addEventListener("input", (e: any) => {
        this.advancedSettings.customLon = e.target.value;
      });
    }
    if (fontSelect) {
      fontSelect.addEventListener("change", (e: any) => {
        this.advancedSettings.fontFamily = e.target.value;
      });
    }

    const showBuildings = document.getElementById("show-buildings") as HTMLInputElement;
    const showRailways = document.getElementById("show-railways") as HTMLInputElement;

    if (showBuildings) {
      showBuildings.addEventListener("change", (e: any) => {
        this.advancedSettings.showBuildings = e.target.checked;
      });
    }
    if (showRailways) {
      showRailways.addEventListener("change", (e: any) => {
        this.advancedSettings.showRailways = e.target.checked;
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        this.advancedSettings = {
          customCity: "",
          customCountry: "",
          customLat: "",
          customLon: "",
          fontFamily: "Geometric Humanist",
          showBuildings: true,
          showRailways: true,
        };
        if (customCity) customCity.value = "";
        if (customCountry) customCountry.value = "";
        if (customLat) customLat.value = "";
        if (customLon) customLon.value = "";
        if (fontSelect) fontSelect.value = "Geometric Humanist";
        if (showBuildings) showBuildings.checked = true;
        if (showRailways) showRailways.checked = true;
      });
    }
  }

  async shareConfiguration() {
    try {
      this.updateURLState();
      const url = globalThis.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        showSuccess(t("download.shareCopied", this.currentLang));
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          showSuccess(t("download.shareCopied", this.currentLang));
        } catch (err) {
          showError(t("download.shareError", this.currentLang));
        }
        document.body.removeChild(textArea);
      }
    } catch (error) {
      showError(t("download.shareError", this.currentLang));
    }
  }

  showConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement("div");
      overlay.className =
        "fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm";
      overlay.innerHTML = `
        <div class="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
          <p class="text-lg font-medium text-slate-900 dark:text-white mb-6">${message}</p>
          <div class="flex gap-3 justify-end">
            <button id="confirm-cancel" class="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
              Cancel
            </button>
            <button id="confirm-ok" class="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors">
              Delete
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      const cleanup = (result: boolean) => {
        document.body.removeChild(overlay);
        resolve(result);
      };
      (overlay.querySelector("#confirm-ok") as HTMLElement).onclick = () => cleanup(true);
      (overlay.querySelector("#confirm-cancel") as HTMLElement).onclick = () => cleanup(false);
      overlay.onclick = (e) => {
        if (e.target === overlay) cleanup(false);
      };
    });
  }

  closeModal() {
    const modal = document.getElementById("poster-modal");
    if (modal) {
      modal.classList.add("hidden");
      modal.classList.remove("flex");
    }
  }

  showLoading(show: boolean, message = "Generating poster...", percent = 0) {
    const spinner = document.getElementById("loading-spinner");
    if (spinner) {
      spinner.style.display = show ? "flex" : "none";
      const msgEl = spinner.querySelector("p");
      if (msgEl) {
        msgEl.textContent = message;
      }
      let progressBar = spinner.querySelector(".progress-bar");
      if (!progressBar && show) {
        progressBar = document.createElement("div");
        progressBar.className =
          "progress-bar w-48 h-2 bg-white/30 rounded-full mt-4 overflow-hidden";
        progressBar.innerHTML =
          '<div class="progress-fill h-full bg-white rounded-full transition-all duration-300"></div>';
        spinner.appendChild(progressBar);
      }
      if (progressBar) {
        const fill = progressBar.querySelector(".progress-fill") as HTMLElement;
        if (fill) {
          fill.style.width = `${percent}%`;
        }
      }
    }
    const btn = document.getElementById("generate-btn");
    if (btn) {
      if (show) {
        btn.innerHTML = `<span class="material-symbols-outlined animate-spin">sync</span> ${
          t("form.stop", this.currentLang)
        }`;
        btn.classList.remove("from-primary", "to-[#6366f1]");
        btn.classList.add("from-red-500", "to-red-600");
      } else {
        btn.innerHTML = `<span class="material-symbols-outlined">refresh</span> ${
          t("form.generate", this.currentLang)
        }`;
        btn.classList.add("from-primary", "to-[#6366f1]");
        btn.classList.remove("from-red-500", "to-red-600");
      }
    }
  }

  fetchMapDataWithProgress(lat: number, lon: number, dist: number) {
    this.isGenerating = true;
    const { showBuildings, showRailways } = this.advancedSettings;
    return new Promise((resolve, reject) => {
      const eventSource = new EventSource(
        `/mapdata-stream?lat=${lat}&lon=${lon}&dist=${dist}&buildings=${showBuildings}&railways=${showRailways}`,
      );
      this.currentEventSource = eventSource;

      eventSource.onmessage = (event) => {
        if (!this.isGenerating) {
          eventSource.close();
          reject(new Error("Generation stopped"));
          return;
        }
        const data = JSON.parse(event.data);

        if (data.type === "progress") {
          this.showLoading(true, data.message, data.percent || 50);
        } else if (data.type === "complete") {
          eventSource.close();
          this.currentEventSource = null;
          resolve(data.data);
        } else if (data.type === "error") {
          eventSource.close();
          this.currentEventSource = null;
          reject(new Error(data.message));
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        this.currentEventSource = null;
        if (this.isGenerating) {
          reject(new Error("Connection lost while fetching map data"));
        } else {
          reject(new Error("Generation stopped"));
        }
      };
    });
  }

  async generatePoster() {
    if (this.isGenerating) {
      this.stopGeneration();
      return;
    }

    const city = (document.getElementById("city") as HTMLInputElement).value.trim();
    const country = (document.getElementById("country") as HTMLInputElement).value.trim();
    const distance =
      parseInt((document.getElementById("distance") as HTMLInputElement).value || "5") * 1000;

    if (!city || !country) {
      showError(t("errors.cityCountry", this.currentLang));
      return;
    }

    const placeholder = document.getElementById("placeholder");
    if (placeholder) placeholder.style.display = "none";

    const useMapLocation = this.mapController.location.isSet;

    try {
      let mapData: any, location: any;

      if (useMapLocation) {
        location = {
          lat: this.mapController.location.lat,
          lon: this.mapController.location.lon,
          radius: distance / 1000,
        };
      } else {
        this.showLoading(true, t("loading.finding", this.currentLang), 5);
        const geocodeData = await geocode(city, country);
        if (!geocodeData) {
          throw new Error(t("errors.geocode", this.currentLang));
        }
        location = geocodeData;
        location.radius = distance / 1000;

        this.mapController.location.lat = location.lat;
        this.mapController.location.lon = location.lon;
        this.mapController.location.isSet = true;
      }

      this.mapController.updateCoordsDisplay(location.lat, location.lon);

      const cacheKey = this.cache.getCacheKey(location.lat, location.lon, distance);
      const isCacheHit = this.cache.isHit(cacheKey);

      if (isCacheHit) {
        this.showLoading(true, t("loading.applying", this.currentLang), 50);
        mapData = this.cache.get().data;
      } else {
        this.showLoading(true, t("loading.fetching", this.currentLang), 10);
        mapData = await this.fetchMapDataWithProgress(location.lat, location.lon, distance);
        this.cache.set(cacheKey, mapData, location);
      }

      this.showLoading(true, "Rendering poster...", 95);
      this.posterRenderer.renderPoster(mapData, city, country, location);

      this.showLoading(true, "Saving to gallery...", 98);
      await this.galleryManager.savePoster(city, country, location.lat, location.lon, distance);

      this.updateURLState();

      this.showLoading(false);

      this.themeManager.updateCurrentThemePreview();

      setTimeout(() => {
        if (!this.isGenerating) this.themeManager.generateAllThemePreviews();
      }, 500);
    } catch (error: any) {
      if (error.message === "Generation stopped") {
        console.log("🛑 Generation stopped by user");
      } else {
        console.error("Error generating poster:", error);
        showError("Failed to generate poster: " + error.message);
      }
      this.showLoading(false);
      const placeholder = document.getElementById("placeholder");
      if (placeholder) placeholder.style.display = "flex";
    } finally {
      this.isGenerating = false;
    }
  }

  stopGeneration() {
    if (this.currentEventSource) {
      this.currentEventSource.close();
      this.currentEventSource = null;
    }
    this.isGenerating = false;
    this.showLoading(false);
    const placeholder = document.getElementById("placeholder");
    if (placeholder && !(this.canvas.dataset as any).hasContent) {
      placeholder.style.display = "flex";
    }
  }

  loadScript(src: string) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  setupLegalModals() {
    const modal = document.getElementById("legal-modal");
    const title = document.getElementById("legal-title");
    const body = document.getElementById("legal-body");
    const close = document.getElementById("legal-close");
    const backdrop = document.getElementById("legal-backdrop");
    const termsBtn = document.getElementById("footer-terms");
    const privacyBtn = document.getElementById("footer-privacy");

    const openModal = (type: "terms" | "privacy") => {
      if (!modal || !title || !body) return;

      const isTerms = type === "terms";
      title.textContent = t(isTerms ? "legal.termsTitle" : "legal.privacyTitle", this.currentLang);
      body.innerHTML = t(isTerms ? "legal.termsContent" : "legal.privacyContent", this.currentLang);

      modal.classList.remove("hidden");
      document.body.style.overflow = "hidden";
    };

    const closeModal = () => {
      modal?.classList.add("hidden");
      document.body.style.overflow = "";
    };

    termsBtn?.addEventListener("click", () => openModal("terms"));
    privacyBtn?.addEventListener("click", () => openModal("privacy"));
    close?.addEventListener("click", closeModal);
    backdrop?.addEventListener("click", closeModal);

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !modal?.classList.contains("hidden")) {
        closeModal();
      }
    });
  }
}
